import { addMinutes } from "@/helpers/dateHelper";

type StoredObject = {
  value: unknown;
  expires?: Date | string | null;
};

export default class LocalStorage {
  static addWithTimout(guid: string, data: unknown, expires?: Date | null): void {
    let expiration = expires;
    if (data === null) {
      expiration = addMinutes(new Date(), 120);
    }
    const object: StoredObject = { value: data, expires: expiration };
    localStorage.setItem(`${window.location.host}_${guid}`, JSON.stringify(object));
    this.clean();
  }

  static getWithTimeout(guid: string): unknown | null {
    this.clean();
    const raw = localStorage.getItem(`${window.location.host}_${guid}`);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw.toString());
      if (parsed && typeof parsed === 'object' && Object.prototype.hasOwnProperty.call(parsed, 'value')) {
        return (parsed as StoredObject).value ?? null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  static delete(guid: string): void {
    localStorage.removeItem(`${window.location.host}_${guid}`);
    this.clean();
  }

  static get(guid: string): string | null {
    this.clean();
    return localStorage.getItem(`${window.location.host}_${guid}`);
  }

  static add(guid: string, data: unknown): void {
    const value = typeof data === "string" ? data : JSON.stringify(data);
    localStorage.setItem(`${window.location.host}_${guid}`, value);
  }

  static clean(): void {
    const keys = Object.keys(localStorage);
    const now = new Date().getTime();

    keys.forEach((key) => {
      if (!key.startsWith(`${window.location.host}_`)) {
        return;
      }

      const storedValue = localStorage.getItem(key);
      if (!storedValue) {
        return;
      }

      let parsedValue: unknown;
      try {
        parsedValue = JSON.parse(storedValue.toString());
      } catch {
        return;
      }

      if (!parsedValue || typeof parsedValue !== "object") {
        return;
      }

      const { expires } = parsedValue as StoredObject;
      if (!expires) {
        return;
      }

      const expiryDate = new Date(expires);
      if (Number.isNaN(expiryDate.getTime())) {
        return;
      }

      if (expiryDate.getTime() - now < 0) {
        localStorage.removeItem(key);
      }
    });
  }
}
